import type { BuyerFundingFlowSession } from "../flows/buyerFunding/types";

export const BUYER_FLOW_PROGRESS_KEY = "buyer-funding-flow-progress";

export type BuyerFlowProgressStep =
  | "invite"
  | "lookup"
  | "device"
  | "persona"
  | "identity"
  | "instructions"
  | "download"
  | "esign"
  | "source"
  | "review"
  | "acknowledge"
  | "closing"
  | "complete"
  /** @deprecated Use `source` — kept for persisted flow-state snapshots */
  | "plaid"
  /** @deprecated Use `review` — kept for persisted flow-state snapshots */
  | "initiate";

export type BuyerFlowProgressSnapshot = {
  fileCode: string;
  step: BuyerFlowProgressStep;
  updatedAt: string;
  buyerName: string;
  officeName: string;
  amountDueCents: number;
  deviceVerified: boolean;
  identityRecorded: boolean;
  instructionsDownloaded: boolean;
  docuSignSigned: boolean;
  plaidRecorded: boolean;
  paymentInitiated: boolean;
  acknowledged: boolean;
};

export function snapshotFromSession(
  session: BuyerFundingFlowSession,
  step: BuyerFlowProgressStep
): BuyerFlowProgressSnapshot {
  return {
    fileCode: session.fileCode,
    step,
    updatedAt: new Date().toISOString(),
    buyerName: session.buyerName,
    officeName: session.officeName,
    amountDueCents: session.amountDue.amountCents,
    deviceVerified: Boolean(session.deviceVerification),
    identityRecorded: Boolean(session.identityVerification),
    instructionsDownloaded: Boolean(session.instructionsDownloadedAt),
    docuSignSigned: Boolean(session.docuSignAck),
    plaidRecorded: Boolean(session.plaid && session.plaid.mode !== "skipped"),
    paymentInitiated: Boolean(session.paymentInitiation),
    acknowledged: Boolean(session.acknowledgedAt)
  };
}

export function writeBuyerFlowProgress(snapshot: BuyerFlowProgressSnapshot): void {
  if (typeof localStorage === "undefined") return;
  const store = readAllBuyerFlowProgress();
  store[snapshot.fileCode.toUpperCase()] = snapshot;
  localStorage.setItem(BUYER_FLOW_PROGRESS_KEY, JSON.stringify(store));
}

export function readBuyerFlowProgress(fileCode: string): BuyerFlowProgressSnapshot | undefined {
  return readAllBuyerFlowProgress()[fileCode.trim().toUpperCase()];
}

export function readAllBuyerFlowProgress(): Record<string, BuyerFlowProgressSnapshot> {
  if (typeof localStorage === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(BUYER_FLOW_PROGRESS_KEY) || "{}") as Record<
      string,
      BuyerFlowProgressSnapshot
    >;
  } catch {
    return {};
  }
}

export const ESCROW_PAYMENT_REQUEST_KEY = "buyer-funding-payment-requests";

export type EscrowPaymentRequestRecord = {
  fileCode: string;
  requestId: string;
  sentAt: string;
  sentBy: string;
  buyerLink: string;
  channel: "email_and_sms";
};

export function recordEscrowPaymentRequest(record: EscrowPaymentRequestRecord): void {
  if (typeof localStorage === "undefined") return;
  const all = readEscrowPaymentRequests();
  all[record.fileCode.toUpperCase()] = record;
  localStorage.setItem(ESCROW_PAYMENT_REQUEST_KEY, JSON.stringify(all));
}

export function readEscrowPaymentRequest(fileCode: string): EscrowPaymentRequestRecord | undefined {
  return readEscrowPaymentRequests()[fileCode.trim().toUpperCase()];
}

export function readEscrowPaymentRequests(): Record<string, EscrowPaymentRequestRecord> {
  if (typeof localStorage === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(ESCROW_PAYMENT_REQUEST_KEY) || "{}") as Record<
      string,
      EscrowPaymentRequestRecord
    >;
  } catch {
    return {};
  }
}

export function buyerFundingLink(fileCode: string, origin = ""): string {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/files/${encodeURIComponent(fileCode.trim().toUpperCase())}`;
}
